(() => {
  const ROOT_SELECTOR = '[data-unbegun-ipad-keyboard]';
  const SERVICE_HANDLE = 'unbegun-personalizer';
  let reconciling = false;

  const shopRoot = () => window.Shopify?.routes?.root || '/';

  function selectionForForm(form) {
    const root = form?.querySelector(ROOT_SELECTOR);
    if (!root) return { enabled: false };

    const enabled = root.querySelector('[data-keyboard-choice]:checked')?.value === 'yes';

    return {
      enabled,
      variantId: Number(root.dataset.keyboardVariantId || 0),
      price: Number(root.dataset.keyboardPrice || 500),
      dimensions: String(
        root.querySelector('[data-keyboard-dimensions]')?.value || ''
      ).trim(),
      productTitle: root.dataset.productTitle || '',
      enabledLabel:
        root.dataset.propertyEnabledLabel || 'iPad met toetsenbord',
      dimensionsLabel:
        root.dataset.propertyDimensionsLabel ||
        'Afmetingen iPad + toetsenbord',
      serviceLabel:
        root.dataset.serviceProductLabel || 'Maatwerk iPad + toetsenbord'
    };
  }

  function setError(root, message = '') {
    const error = root?.querySelector('[data-keyboard-error]');
    const input = root?.querySelector('[data-keyboard-dimensions]');
    const hasError = Boolean(message);

    if (error) {
      if (hasError) error.textContent = message;
      error.hidden = !hasError;
    }

    if (input) {
      input.classList.toggle('is-error', hasError);
      input.setAttribute('aria-invalid', hasError ? 'true' : 'false');

      if (error?.id) {
        input.setAttribute('aria-describedby', error.id);
      }
    }
  }

  function validate(form, { focus = false } = {}) {
    const root = form?.querySelector(ROOT_SELECTOR);
    const selection = selectionForForm(form);

    if (!selection.enabled) {
      setError(root);
      return true;
    }

    if (selection.dimensions) {
      setError(root);
      return true;
    }

    const errorText =
      root?.querySelector('[data-keyboard-error]')?.textContent ||
      'Vul de afmetingen in.';

    setError(root, errorText);

    if (focus) {
      root?.querySelector('[data-keyboard-dimensions]')?.focus();
    }

    return false;
  }

  function appendToPayload(payload, form, quantity, linkKey) {
    const keyboard = selectionForForm(form);

    if (
      !keyboard.enabled ||
      !keyboard.variantId ||
      !payload?.items?.length
    ) {
      return keyboard;
    }

    const base = payload.items[0];

    base.properties = Object.assign({}, base.properties || {}, {
      [keyboard.enabledLabel]: 'Ja',
      [keyboard.dimensionsLabel]: keyboard.dimensions,
      '_Toetsenbord koppeling': linkKey,
      '_Toetsenbord variant': String(keyboard.variantId)
    });

    payload.items.push({
      id: keyboard.variantId,
      quantity,
      properties: {
        'Bij product': keyboard.productTitle,
        [keyboard.dimensionsLabel]: keyboard.dimensions,
        '_Toetsenbord koppeling': linkKey,
        '_Unbegun service': 'ipad-keyboard'
      }
    });

    return keyboard;
  }

  async function reconcile() {
    if (reconciling) return false;

    reconciling = true;

    try {
      const response = await fetch(`${shopRoot()}cart.js?_=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return false;

      const cart = await response.json();
      const desired = new Map();
      const services = new Map();
      const bases = new Map();

      cart.items.forEach((item) => {
        const link = item.properties?.['_Toetsenbord koppeling'];

        if (!link) return;

        const isService =
          item.handle === SERVICE_HANDLE &&
          item.properties?.['_Unbegun service'] === 'ipad-keyboard';

        if (isService) {
          if (!services.has(link)) {
            services.set(link, []);
          }

          services.get(link).push(item);
        } else {
          desired.set(
            link,
            (desired.get(link) || 0) + Number(item.quantity || 0)
          );

          if (!bases.has(link)) {
            bases.set(link, item);
          }
        }
      });

      const updates = {};
      const additions = [];

      services.forEach((items, link) => {
        const qty = desired.get(link) || 0;

        items.forEach((item, index) => {
          const wanted = index === 0 ? qty : 0;

          if (Number(item.quantity || 0) !== wanted) {
            updates[item.key] = wanted;
          }
        });
      });

      desired.forEach((qty, link) => {
        if (!qty || services.has(link)) return;

        const base = bases.get(link);
        const variantId = Number(
          base?.properties?.['_Toetsenbord variant'] || 0
        );

        if (!variantId) return;

        const dimensionsEntry = Object.entries(
          base?.properties || {}
        ).find(([key]) => {
          const normalized = String(key).toLowerCase();

          return (
            normalized.includes('afmet') ||
            normalized.includes('dimension') ||
            normalized.includes('maße') ||
            normalized.includes('dimensions')
          );
        });

        const dimensions = String(dimensionsEntry?.[1] || '');

        additions.push({
          id: variantId,
          quantity: qty,
          properties: {
            'Bij product':
              base?.product_title || base?.title || 'iPad sleeve',
            ...(dimensionsEntry
              ? { [dimensionsEntry[0]]: dimensions }
              : {}),
            '_Toetsenbord koppeling': link,
            '_Unbegun service': 'ipad-keyboard'
          }
        });
      });

      let changed = false;

      if (Object.keys(updates).length) {
        const update = await fetch(`${shopRoot()}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates })
        });

        changed = update.ok || changed;
      }

      if (additions.length) {
        const add = await fetch(`${shopRoot()}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: additions })
        });

        changed = add.ok || changed;
      }

      return changed;
    } catch (error) {
      console.error(
        '[UNBEGUN iPad Keyboard] Reconcile failed',
        error
      );

      return false;
    } finally {
      reconciling = false;
    }
  }

  function updateSummary(form) {
    window.UnbegunGiftWrap?.updateSummary?.(form);
  }

  async function addKeyboardOnly(event, root, form) {
    const keyboard = selectionForForm(form);

    if (!keyboard.enabled) return;

    if (
      form.querySelector('[data-personalizer-toggle]')?.checked
    ) {
      return;
    }

    if (
      form.querySelector('[data-gift-wrap-toggle]')?.checked
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!validate(form, { focus: true })) return;

    const variantId = Number(
      form.querySelector('[name="id"]')?.value || 0
    );

    const quantity = Math.max(
      1,
      Number(form.querySelector('[name="quantity"]')?.value || 1)
    );

    if (!variantId || !keyboard.variantId) return;

    const link = `UK-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)
      .toUpperCase()}`;

    const payload = {
      items: [
        {
          id: variantId,
          quantity,
          properties: {}
        }
      ]
    };

    appendToPayload(payload, form, quantity, link);

    window.UnbegunPreorder?.appendToPayload?.(
      payload,
      form
    );

    const button = form.querySelector('[data-add-button]');

    button?.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.description ||
            'Toevoegen aan winkelwagen is niet gelukt.'
        );
      }

      if (window.UnbegunCart?.openAfterMutation) {
        await window.UnbegunCart.openAfterMutation();
      } else if (window.UnbegunCart?.open) {
        await window.UnbegunCart.open();
      } else {
        window.location.assign(`${shopRoot()}cart`);
      }
    } catch (error) {
      console.error(
        '[UNBEGUN iPad Keyboard]',
        error
      );

      setError(
        root,
        error.message ||
          'Er ging iets mis. Probeer het opnieuw.'
      );
    } finally {
      button?.removeAttribute('disabled');
    }
  }

  function init(root) {
    if (root.dataset.keyboardReady === 'true') return;

    const form = root.closest('form[data-product-form]');

    if (!form) return;

    root.dataset.keyboardReady = 'true';

    const choices = [
      ...root.querySelectorAll('[data-keyboard-choice]')
    ];

    const content = root.querySelector(
      '[data-keyboard-content]'
    );

    const dimensions = root.querySelector(
      '[data-keyboard-dimensions]'
    );

    const button = form.querySelector(
      '[data-add-button]'
    );

    const render = () => {
      const enabled =
        choices.find((choice) => choice.checked)?.value ===
        'yes';

      content.hidden = !enabled;

      choices.forEach((choice) => {
        choice
          .closest('.unbegun-ipad-keyboard__choice')
          ?.classList.toggle(
            'is-selected',
            choice.checked
          );
      });

      if (!enabled) {
        setError(root);
      }

      updateSummary(form);
    };

    choices.forEach((choice) => {
      choice.addEventListener('change', render);
    });

    dimensions?.addEventListener('input', () => {
      if (dimensions.value.trim()) {
        setError(root);
      }

      updateSummary(form);
    });

    button?.addEventListener(
      'click',
      (event) =>
        addKeyboardOnly(event, root, form),
      true
    );

    form.addEventListener(
      'submit',
      (event) =>
        addKeyboardOnly(event, root, form),
      true
    );

    render();
  }

  function boot() {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    boot
  );

  window.UnbegunIpadKeyboard = {
    getSelection: selectionForForm,
    appendToPayload,
    reconcile,
    validate,
    updateSummary
  };
})();